import { useEffect, useRef } from 'react';
import apiService from '../../services/ApiService';
import type { AdCreative } from './homeTypes';

/**
 * Fire a single ad impression when the element is ≥50% visible for ≥1s.
 * Once per placement instance (hook mount / creative id).
 */
export function useSponsoredImpression(
  creative: AdCreative | null | undefined,
  options?: { disabled?: boolean }
) {
  const ref = useRef<HTMLElement | null>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    firedRef.current = false;
  }, [creative?.creative_id, creative?.placement]);

  useEffect(() => {
    if (!creative || options?.disabled) return;
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    let timer: ReturnType<typeof setTimeout> | null = null;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
          if (timer || firedRef.current) return;
          timer = setTimeout(() => {
            if (firedRef.current) return;
            firedRef.current = true;
            void apiService.recordAdImpression({
              creative_id: creative.creative_id,
              campaign_id: creative.campaign_id,
              placement: creative.placement,
            });
            void apiService.trackEvent('ad_impression', {
              metadata: {
                creative_id: creative.creative_id,
                campaign_id: creative.campaign_id,
                placement: creative.placement,
              },
            });
          }, 1000);
        } else if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      },
      { threshold: [0, 0.5, 1] }
    );

    observer.observe(el);
    return () => {
      if (timer) clearTimeout(timer);
      observer.disconnect();
    };
  }, [creative, options?.disabled]);

  return ref;
}
